import React, { useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import Logo from '../components/Logo';
import supabase from '../supabase/supabase';

const { FiMail, FiLock, FiArrowRight, FiUser, FiEye, FiEyeOff } = FiIcons;

const Login = () => {
  const [mode, setMode] = useState('login');
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    fullName: '',
  });

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleAuth = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setMessage({ type: '', text: '' });

    try {
      if (mode === 'login') {
        const { error } = await supabase.auth.signInWithPassword({
          email: formData.email,
          password: formData.password,
        });
        if (error) {
          setIsLoading(false);
          throw error;
        }
      } else if (mode === 'signup') {
        if (formData.password !== formData.confirmPassword) {
          setIsLoading(false);
          throw new Error('As senhas não coincidem');
        }
        
        const { error } = await supabase.auth.signUp({
          email: formData.email,
          password: formData.password,
          options: {
            data: { full_name: formData.fullName }
          }
        });
        
        if (error) {
          setIsLoading(false);
          throw error;
        }
        setMessage({ type: 'success', text: 'Conta criada! Você já pode tentar entrar.' });
        setIsLoading(false);
      } else if (mode === 'forgot-password') {
        const { error } = await supabase.auth.resetPasswordForEmail(formData.email, {
          redirectTo: window.location.origin + '/update-password',
        });
        if (error) {
          setIsLoading(false);
          throw error;
        }
        setMessage({ type: 'success', text: 'E-mail enviado! Verifique sua caixa de entrada.' });
        setIsLoading(false);
      }
    } catch (error) {
      setMessage({ type: 'error', text: error.message || 'Erro na autenticação.' });
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4 relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute top-[-10%] right-[-10%] w-[500px] h-[500px] bg-indigo-200/20 rounded-full blur-[100px]" />
        <div className="absolute bottom-[-10%] left-[-10%] w-[500px] h-[500px] bg-blue-200/20 rounded-full blur-[100px]" />
      </div>

      <motion.div 
        initial={{ opacity: 0, scale: 0.95 }} 
        animate={{ opacity: 1, scale: 1 }} 
        className="bg-white/80 backdrop-blur-xl rounded-[2.5rem] shadow-2xl shadow-indigo-100/50 p-8 sm:p-12 w-full max-w-md border border-white/50 relative z-10"
      >
        <div className="text-center mb-10">
          <div className="flex justify-center mb-6 scale-125">
             <Logo className="w-12 h-12" showText={false} />
          </div>
          <h1 className="text-3xl font-black text-slate-900 italic uppercase tracking-tight leading-tight">
            Teste <span className="text-indigo-600">Psicológico</span> <br/> Online
          </h1>
          <p className="text-slate-400 text-[10px] font-black uppercase tracking-[0.3em] mt-3">Acesso Profissional</p>
        </div>

        {message.text && (
          <motion.div 
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className={`p-4 rounded-2xl mb-8 text-xs font-bold border flex items-center gap-3 ${
              message.type === 'success' ? 'bg-green-50 text-green-700 border-green-100' : 'bg-red-50 text-red-700 border-red-100'
            }`}
          >
            <div className={`w-2 h-2 rounded-full shrink-0 ${message.type === 'success' ? 'bg-green-500' : 'bg-red-500'}`} />
            {message.text}
          </motion.div>
        )}

        <form onSubmit={handleAuth} className="space-y-5">
          {mode === 'signup' && (
            <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}>
              <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5 ml-1">Nome Completo</label>
              <div className="relative">
                <SafeIcon icon={FiUser} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-300" />
                <input 
                  name="fullName" 
                  type="text" 
                  required 
                  onChange={handleInputChange} 
                  className="w-full pl-12 pr-4 py-4 bg-slate-50 border border-slate-100 rounded-2xl outline-none focus:ring-2 focus:ring-indigo-500 focus:bg-white transition-all text-sm font-bold text-slate-800 placeholder:text-slate-300" 
                  placeholder="Seu nome completo"
                />
              </div>
            </motion.div>
          )}

          <div>
            <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5 ml-1">E-mail Profissional</label>
            <div className="relative">
              <SafeIcon icon={FiMail} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-300" />
              <input 
                name="email" 
                type="email" 
                required 
                onChange={handleInputChange} 
                className="w-full pl-12 pr-4 py-4 bg-slate-50 border border-slate-100 rounded-2xl outline-none focus:ring-2 focus:ring-indigo-500 focus:bg-white transition-all text-sm font-bold text-slate-800 placeholder:text-slate-300" 
                placeholder="exemplo@clinica.com" 
              />
            </div>
          </div>

          {mode !== 'forgot-password' && (
            <>
              <div>
                <div className="flex justify-between items-center mb-1.5 ml-1">
                  <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest">Senha</label>
                  {mode === 'login' && (
                    <button type="button" onClick={() => setMode('forgot-password')} className="text-[10px] font-black text-indigo-600 uppercase hover:underline">Esqueci a senha</button>
                  )}
                </div>
                <div className="relative">
                  <SafeIcon icon={FiLock} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-300" />
                  <input 
                    name="password" 
                    type={showPassword ? "text" : "password"} 
                    required 
                    onChange={handleInputChange} 
                    className="w-full pl-12 pr-12 py-4 bg-slate-50 border border-slate-100 rounded-2xl outline-none focus:ring-2 focus:ring-indigo-500 focus:bg-white transition-all text-sm font-bold text-slate-800 placeholder:text-slate-300" 
                    placeholder="••••••••" 
                  />
                  <button 
                    type="button" 
                    onClick={() => setShowPassword(!showPassword)} 
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-300 hover:text-slate-500 transition-colors"
                  >
                    <SafeIcon icon={showPassword ? FiEyeOff : FiEye} />
                  </button>
                </div>
              </div>

              {mode === 'signup' && (
                <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="mt-4">
                  <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5 ml-1">Confirmar Senha</label>
                  <div className="relative">
                    <SafeIcon icon={FiLock} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-300" />
                    <input 
                      name="confirmPassword" 
                      type={showPassword ? "text" : "password"} 
                      required 
                      onChange={handleInputChange} 
                      className="w-full pl-12 pr-4 py-4 bg-slate-50 border border-slate-100 rounded-2xl outline-none focus:ring-2 focus:ring-indigo-500 focus:bg-white transition-all text-sm font-bold text-slate-800 placeholder:text-slate-300" 
                      placeholder="Repita sua senha"
                    />
                  </div>
                </motion.div>
              )}
            </>
          )}

          <button 
            disabled={isLoading} 
            type="submit" 
            className="w-full bg-slate-900 hover:bg-indigo-600 text-white font-black py-5 rounded-2xl transition-all flex items-center justify-center shadow-xl shadow-slate-200 mt-6 disabled:opacity-50 group"
          >
            {isLoading ? (
              <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <span className="uppercase tracking-[0.2em] text-xs flex items-center group-hover:scale-105 transition-transform">
                {mode === 'login' ? 'Entrar no Sistema' : mode === 'signup' ? 'Solicitar Acesso' : 'Recuperar Senha'}
                <SafeIcon icon={FiArrowRight} className="ml-3" />
              </span>
            )}
          </button>
        </form>

        <div className="mt-10 text-center flex flex-col space-y-4">
          <button 
            onClick={() => setMode(mode === 'signup' ? 'login' : 'signup')} 
            className="text-[10px] font-black text-slate-400 uppercase hover:text-indigo-600 transition-colors tracking-widest"
          >
            {mode === 'signup' ? 'Já possui acesso? • Entrar' : 'Novo por aqui? • Criar conta'}
          </button>
        </div>
      </motion.div>
    </div>
  );
};

export default Login;